import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { mailtoHref, CONTACT } from './utils/obfuscate';
import CookieConsent from './components/CookieConsent';
import Skeleton from './components/Skeleton';
import './App.css';

const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const About = lazy(() => import('./pages/About'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Contact = lazy(() => import('./pages/Contact'));
const Projects = lazy(() => import('./pages/Projects'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

const WHATSAPP_MESSAGE = encodeURIComponent("Hi DrewLink, I need electrical help.");
const WHATSAPP_NUMBER = CONTACT.waNumber;

const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/services", label: "Services" },
  { href: "/projects", label: "Projects" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <Router>
      <div className="App">
        <Header />
        <main>
          <Suspense fallback={<Skeleton />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/about" element={<About />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/:serviceSlug/:suburbSlug" element={<LocationPage />} />
            </Routes>
          </Suspense>
        </main>

        <PullSwitch theme={theme} setTheme={setTheme} />

        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float"
          aria-label="Chat on WhatsApp"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>

        <Footer />
        <CookieConsent />
      </div>
    </Router>
  );
}

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <header className="site-header">
      <div className="header-inner container">
        <Link to="/" className="site-logo" onClick={() => setIsOpen(false)}>
          <img src="/drewlink.png" alt="DrewLink Electrical" className="logo-img" width="44" height="44" />
          <span className="logo-text">
            DrewLink<br/>
            <em>Electrical</em>
          </span>
        </Link>

        <nav className="desktop-nav" aria-label="Desktop navigation">
          {NAV_LINKS.map(link => (
            <Link
              key={link.href}
              to={link.href}
              className={location.pathname === link.href ? 'active' : ''}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <a href={'tel:' + CONTACT.phone} className="header-call-btn">
          <span className="btn-label">Emergency Call</span>
          <span className="btn-number">{CONTACT.phoneDisplay}</span>
        </a>

        <button
          className={`hamburger-btn ${isOpen ? 'is-open' : ''}`}
          onClick={() => setIsOpen(prev => !prev)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </div>

      {isOpen && (
        <div className="mobile-nav-backdrop" onClick={() => setIsOpen(false)} aria-hidden="true" />
      )}

      <nav className={`mobile-nav-drawer ${isOpen ? 'is-open' : ''}`} aria-label="Mobile navigation">
        <ul className="mobile-nav-list">
          {NAV_LINKS.map(link => (
            <li key={link.href}>
              <Link
                to={link.href}
                className={`mobile-nav-link ${location.pathname === link.href ? 'is-active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mobile-nav-cta">
          <a href={'tel:' + CONTACT.phone} className="mobile-nav-call-btn">
            Call Now — Electrician
          </a>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mobile-nav-whatsapp-btn"
          >
            WhatsApp Us
          </a>
        </div>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-inner container">
        <div className="footer-brand">
          <div className="footer-logo">
            <img src="/drewlink.png" alt="DrewLink Electrical" className="footer-logo-img" width="48" height="48" loading="lazy" />
          </div>
          <p>Cape Town, Western Cape</p>
        </div>
        <div className="footer-contact">
          <p className="footer-contact-label">Get in touch</p>
          <a href={'tel:' + CONTACT.phone}>{CONTACT.phoneDisplay}</a>
          <a href={mailtoHref(CONTACT.email)}>{CONTACT.email}</a>
        </div>
        <div className="footer-certs">
          <p className="footer-contact-label">Certifications</p>
          <span className="cert-badge">ECA Registered</span>
          <span className="cert-badge">CoC Certified</span>
          <span className="cert-badge">ECB Compliant</span>
        </div>
      </div>
      <div className="footer-base">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} DrewLink Engineering (PTY) LTD · All rights reserved.</p>
          <p className="footer-made-by">Made by <a href="https://www.sohera.co.za/" rel="noopener noreferrer">Sohera</a></p>
        </div>
      </div>
    </footer>
  );
}

function PullSwitch({ theme, setTheme }) {
  const [swinging, setSwinging] = useState(false);

  const toggle = () => {
    setSwinging(true);
    setTheme(t => t === 'dark' ? 'light' : 'dark');
  };

  const isLight = theme === 'light';

  return (
    <button
      className="pull-switch"
      onClick={toggle}
      aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
    >
      <svg viewBox="0 0 100 160" width="90" height="144" style={{ overflow: 'visible', display: 'block' }}>
        <defs>
          <filter id="warmglow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur" />
            <feBlend in="SourceGraphic" in2="blur" mode="normal" />
          </filter>
          <radialGradient id="bulbgrad" cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor="#fffde7" />
            <stop offset="100%" stopColor="#fef08a" />
          </radialGradient>
        </defs>

        {/* Shadow */}
        <rect x="28" y="18" width="50" height="70" rx="7"
          fill="#000" opacity={isLight ? '0.1' : '0.55'} />

        {/* Plate */}
        <rect id="plate" x="25" y="13" width="50" height="70" rx="7"
          fill={isLight ? '#dde4ed' : '#1e293b'}
          stroke={isLight ? '#b8c4d0' : '#334155'} strokeWidth="1.2" />

        {/* Plate sheen */}
        <rect x="25" y="13" width="50" height="15" rx="7"
          fill="#ffffff" opacity={isLight ? '0.55' : '0.04'} />
        <rect x="25" y="27" width="50" height="1"
          fill="#ffffff" opacity="0.04" />

        {/* Screws */}
        <circle cx="50" cy="20.5" r="2.2" fill={isLight ? '#b8c4d0' : '#334155'} />
        <line x1="48.2" y1="20.5" x2="51.8" y2="20.5"
          stroke={isLight ? '#dde4ed' : '#1e293b'} strokeWidth="0.8" />
        <circle cx="50" cy="75.5" r="2.2" fill={isLight ? '#b8c4d0' : '#334155'} />
        <line x1="48.2" y1="75.5" x2="51.8" y2="75.5"
          stroke={isLight ? '#dde4ed' : '#1e293b'} strokeWidth="0.8" />

        {/* Bulb window */}
        <circle cx="50" cy="48" r="19" fill={isLight ? '#c0c9d4' : '#263340'} />
        <circle cx="50" cy="48" r="17.5" fill="none"
          stroke={isLight ? '#adb7c2' : '#1a2535'} strokeWidth="1" />
        <circle cx="50" cy="48" r="15.5" fill={isLight ? '#fef9c3' : '#0c1523'} />

        {/* Bulb icon */}
        <g fill={isLight ? 'url(#bulbgrad)' : 'none'}
          stroke={isLight ? '#d97706' : '#3d5068'}
          strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
          style={isLight ? { filter: 'url(#warmglow)' } : undefined}>
          <path d="M50,39.5 A7.5,7.5 0 0,0 43.5,51.5
                   C43.5,54.2 45.2,56 46,57.5
                   L46,59.5 Q46,60.5 47,60.5
                   L53,60.5 Q54,60.5 54,59.5
                   L54,57.5 C54.8,56 56.5,54.2 56.5,51.5
                   A7.5,7.5 0 0,0 50,39.5 Z" />
          <line x1="47" y1="54.5" x2="53" y2="54.5" />
          <line x1="47.5" y1="57.5" x2="52.5" y2="57.5" />
        </g>

        {/* Exit hole */}
        <ellipse cx="50" cy="83.5" rx="4.5" ry="1.8" fill="#000" opacity="0.5" />

        {/* Pull chain */}
        <g className={`pull-chain ${swinging ? 'swinging' : ''}`}
           onAnimationEnd={() => setSwinging(false)}>
          <ellipse cx="50" cy="88.5"  rx="2.8" ry="2"
            fill={isLight ? '#92400e' : '#38bdf8'} opacity="0.85" />
          <ellipse cx="50" cy="94"    rx="2"   ry="2.8"
            fill={isLight ? '#92400e' : '#38bdf8'} opacity="0.85" />
          <ellipse cx="50" cy="99.5"  rx="2.8" ry="2"
            fill={isLight ? '#92400e' : '#38bdf8'} opacity="0.85" />
          <ellipse cx="50" cy="105"   rx="2"   ry="2.8"
            fill={isLight ? '#92400e' : '#38bdf8'} opacity="0.85" />
          <ellipse cx="50" cy="110.5" rx="2.8" ry="2"
            fill={isLight ? '#92400e' : '#38bdf8'} opacity="0.85" />
          <circle cx="50" cy="119" r="6"
            fill={isLight ? '#b45309' : '#38bdf8'} />
          <circle cx="50" cy="119" r="3.8" fill="none"
            stroke={isLight ? '#fef9c3' : '#0f172a'} strokeWidth="1.2" opacity="0.25" />
          <circle cx="50" cy="119" r="1.2"
            fill={isLight ? '#0f172a' : '#0f172a'} opacity="0.2" />
        </g>
      </svg>
    </button>
  );
}

export default App;
